import { reloadApp } from "../../../engine/storage/index.js";
/**
 * Update Manager
 *
 * Handles frontend update notifications from the server.
 * Supports three update levels:
 * 1. Silent - Auto-refresh when user is idle
 * 2. Suggest - Non-blocking banner, user chooses when to refresh
 * 3. Require - Blocking banner, sync disabled until refresh
 *
 * The interface's: the engine announces a newer version and stops sending
 * when told to (engine/sync/app-version.js); this decides what the person sees
 * and when the page reloads (Phase 5b, step 2: moved from sync/).
 */

import { getVersion, stopSyncForUpdate, isSyncStoppedForUpdate } from "../../../engine/sync/app-version.js";
import { on, Events } from "../../../engine/core/events.js";
import { t } from "../../../shared/i18n/index.js";

// Injected dependency to avoid circular import with app.js
let _getCurrentUser = () => null;

/**
 * Set the getCurrentUser function (called from main.js to break circular dep)
 * @param {Function} fn - Function that returns the current user
 */
export function setGetCurrentUser(fn) {
  _getCurrentUser = fn;
}

/**
 * Update levels
 */
export const UpdateLevel = {
  SILENT: 1,
  SUGGEST: 2,
  REQUIRE: 3,
};

/**
 * UpdateManager class
 * Singleton that handles update notifications across all WebSocket connections
 */
export class UpdateManager {
  constructor({ reload = reloadApp } = {}) {
    this._reload = reload;
    this._retryReloadAt = 0;
    this.currentVersion = getVersion();
    this.pendingUpdate = null;
    this.isIdle = true;
    this.idleTimeout = null;
    this.idleDelayMs = 2000; // 2 seconds of no activity = idle
    this.extendedIdleDelayMs = 10000; // 10 seconds for contentEditable fields
    this.lastActivityTime = Date.now();
    this.bannerElement = null;
    this.syncDisabled = false;
    this._isRefreshing = false; // Guard against multiple simultaneous refreshes

    this._setupIdleDetection();

    console.log(
      "[UpdateManager] Initialized with version:",
      this.currentVersion,
    );
  }

  /**
   * Handle an update-available message from the server
   * @param {Object} data - { version, level, message? }
   */
  handleUpdateAvailable({ version, level, message }) {
    console.log("[UpdateManager] Update available:", version, "level:", level);

    // Ignore updates when not logged in
    if (!_getCurrentUser()) {
      console.log(
        "[UpdateManager] Not logged in, ignoring update notification",
      );
      return;
    }

    // Deduplicate - ignore if already processing this version
    if (this.pendingUpdate?.version === version) {
      console.log("[UpdateManager] Already processing this version, ignoring");
      return;
    }

    // Compare versions - only act if newer
    if (!this._isNewer(version)) {
      return;
    }

    // Store pending update (most restrictive level wins if multiple)
    if (this.pendingUpdate && this.pendingUpdate.level > level) {
      console.log(
        "[UpdateManager] Existing update has higher level, keeping it",
      );
      return;
    }

    this.pendingUpdate = { version, level, message };

    // Clear any existing silent update interval when switching levels
    if (this._silentUpdateInterval && level !== UpdateLevel.SILENT) {
      clearInterval(this._silentUpdateInterval);
      this._silentUpdateInterval = null;
    }

    switch (level) {
      case UpdateLevel.SUGGEST:
        this._showSuggestBanner(message);
        break;
      case UpdateLevel.REQUIRE:
        this._showRequireBanner(message);
        this._disableSync();
        break;
      case UpdateLevel.SILENT:
        this._scheduleSilentUpdate();
        break;
      default:
        console.warn("[UpdateManager] Unknown update level:", level);
        this._showSuggestBanner(message);
    }
  }

  /**
   * Check if a version is newer than current
   * @param {string} version - Version string (format: hash-timestamp)
   * @returns {boolean}
   */
  _isNewer(version) {
    if (!version || version === "unknown-0") {
      console.log("[UpdateManager] Invalid server version:", version);
      return false;
    }

    // Dev builds ship `APP_VERSION = "dev"` (no timestamp suffix). The
    // running JS is whatever the dev server is currently serving — if we
    // need newer code, Vite's HMR or a manual refresh handles it. Treating
    // every server-version push as "newer" would auto-reload the page on
    // every idle tick during normal use (e.g. zoom/pan pauses in bubble
    // view), which is not what dev mode should do.
    if (this.currentVersion === "dev") {
      return false;
    }

    const newTimestamp = parseInt(version.split("-")[1] || "0");
    const currentTimestamp = parseInt(this.currentVersion.split("-")[1] || "0");

    if (newTimestamp > currentTimestamp) {
      console.log(
        `[UpdateManager] Newer version available (client: ${this.currentVersion}, server: ${version})`,
      );
      return true;
    } else if (newTimestamp === currentTimestamp) {
      console.log(
        `[UpdateManager] Version matches (client: ${this.currentVersion}, server: ${version})`,
      );
    } else {
      console.log(
        `[UpdateManager] Client version newer (client: ${this.currentVersion}, server: ${version})`,
      );
    }

    return false;
  }

  /**
   * Set up idle detection
   */
  _setupIdleDetection() {
    // Activity handlers only stamp a timestamp — no timer churn. The previous
    // clearTimeout+setTimeout per mousemove/scroll event allocated and tore
    // down a timer hundreds of times per second during interaction. A single
    // coarse interval (1/10 of the idle delay) flips the idle flag instead.
    const noteActivity = () => {
      this.isIdle = false;
      this.lastActivityTime = Date.now();
    };

    window.addEventListener("mousemove", noteActivity, { passive: true });
    window.addEventListener("mousedown", noteActivity, { passive: true });
    window.addEventListener("keydown", noteActivity, { passive: true });
    window.addEventListener("touchstart", noteActivity, { passive: true });
    window.addEventListener("scroll", noteActivity, { passive: true });

    const checkEvery = Math.max(1000, Math.floor(this.idleDelayMs / 10));
    this.idleCheckInterval = setInterval(() => {
      if (!this.isIdle && Date.now() - this.lastActivityTime >= this.idleDelayMs) {
        this.isIdle = true;
        this._checkPendingSilentUpdate();
      }
    }, checkEvery);

    // Start as idle
    this.isIdle = true;
  }

  /**
   * Check if user is busy (shouldn't be interrupted)
   * For contentEditable fields (like TipTap editor), use extended idle timeout
   * @returns {boolean}
   */
  _isUserBusy() {
    // Always busy during drag, modal, or context menu
    if (
      document.querySelector(".dragging") !== null ||
      document.querySelector(".modal-bg.active") !== null ||
      document.querySelector("#context-menu:not(.hidden)") !== null
    ) {
      return true;
    }

    // For input/textarea, busy if focused
    if (
      document.activeElement?.tagName === "INPUT" ||
      document.activeElement?.tagName === "TEXTAREA"
    ) {
      return true;
    }

    // For contentEditable (TipTap, etc.), use extended idle timeout
    // User is only considered busy if they've been active in the last 10 seconds
    if (document.activeElement?.isContentEditable) {
      const timeSinceActivity = Date.now() - this.lastActivityTime;
      return timeSinceActivity < this.extendedIdleDelayMs;
    }

    return false;
  }

  /**
   * Schedule a silent update when user is idle
   */
  _scheduleSilentUpdate() {
    console.log("[UpdateManager] Scheduling silent update");
    // Check immediately
    this._checkPendingSilentUpdate();
    // Also set up a periodic check in case we miss the idle event
    if (!this._silentUpdateInterval) {
      this._silentUpdateInterval = setInterval(() => {
        this._checkPendingSilentUpdate();
      }, 1000);
    }
  }

  /**
   * Check if we should perform a pending silent update
   */
  _checkPendingSilentUpdate() {
    if (
      !this.pendingUpdate ||
      this.pendingUpdate.level !== UpdateLevel.SILENT
    ) {
      // Clear interval if no pending silent update
      if (this._silentUpdateInterval) {
        clearInterval(this._silentUpdateInterval);
        this._silentUpdateInterval = null;
      }
      return;
    }

    if (Date.now() < this._retryReloadAt || this._isRefreshing) return;
    const busy = this._isUserBusy();
    if (this.isIdle && !busy) {
      console.log("[UpdateManager] User is idle, performing silent update");
      if (this._silentUpdateInterval) {
        clearInterval(this._silentUpdateInterval);
        this._silentUpdateInterval = null;
      }
      this._performSilentUpdate();
    } else {
      console.log(
        "[UpdateManager] User is busy, waiting for idle. isIdle:",
        this.isIdle,
        "busy:",
        busy,
        "activeElement:",
        document.activeElement?.tagName,
        document.activeElement?.id,
      );
    }
  }

  /**
   * Perform a silent update (save state and refresh)
   * Waits for server to be ready before refreshing
   */
  async _performSilentUpdate() {
    // Guard against multiple simultaneous refreshes
    if (this._isRefreshing) {
      console.log("[UpdateManager] Refresh already in progress, skipping");
      return;
    }
    this._isRefreshing = true;

    let reloading = false;
    try {
      // Save UI state
      const uiState = this._captureUIState();
      sessionStorage.setItem("orbital_ui_state", JSON.stringify(uiState));
      sessionStorage.setItem("orbital_silent_refresh", "true");

      console.log(
        "[UpdateManager] Performing silent refresh, waiting for server...",
      );

      // Wait for server to be stable before refreshing
      // Fetch index.html and verify the new JS asset is available
      const maxWaitMs = 30000;
      const requiredSuccesses = 3;
      let successCount = 0;
      const startTime = Date.now();

      while (Date.now() - startTime < maxWaitMs) {
        try {
          // Fetch index.html with cache bypass to get the new version
          const indexResponse = await fetch("/?_t=" + Date.now());
          if (!indexResponse.ok) {
            successCount = 0;
            console.log("[UpdateManager] Server not ready (index.html failed)");
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          }

          // Extract JS filename from index.html
          const html = await indexResponse.text();
          const jsMatch = html.match(/assets\/(main-[^"]+\.js)/);
          if (!jsMatch) {
            successCount = 0;
            console.log("[UpdateManager] Could not find JS asset in index.html");
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          }

          const jsFile = "/assets/" + jsMatch[1];

          // Verify the JS asset is actually available
          const jsResponse = await fetch(jsFile, { method: "HEAD" });
          if (jsResponse.ok) {
            successCount++;
            console.log(
              `[UpdateManager] New assets ready (${successCount}/${requiredSuccesses}): ${jsMatch[1]}`,
            );
            if (successCount >= requiredSuccesses) {
              console.log("[UpdateManager] Server stable, refreshing now");
              reloading = await this._reload();
              return;
            }
          } else {
            successCount = 0;
            console.log(`[UpdateManager] JS asset not ready: ${jsFile}`);
          }
        } catch (e) {
          successCount = 0;
          console.log("[UpdateManager] Server not ready, waiting...", e.message);
        }
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Give up and try anyway
      console.log(
        "[UpdateManager] Timeout waiting for server, refreshing anyway",
      );
      reloading = await this._reload();
    } catch (error) {
      console.error("[UpdateManager] Refresh cancelled:", error);
    } finally {
      this._isRefreshing = false;
      if (!reloading) {
        sessionStorage.removeItem("orbital_silent_refresh");
        this._retryReloadAt = Date.now() + 30000;
        if (this.pendingUpdate?.level === UpdateLevel.SILENT && !this._silentUpdateInterval) {
          this._silentUpdateInterval = setInterval(() => this._checkPendingSilentUpdate(), 1000);
        }
      }
    }
  }

  /**
   * Capture current UI state for restoration after refresh
   * @returns {Object}
   */
  _captureUIState() {
    // Try to get state from global app state if available
    const state = window.appState || {};

    return {
      selectedNodeId: state.selectedId || null,
      currentView: state.currentView || null,
      scrollPosition: { x: window.scrollX, y: window.scrollY },
      zoom: state.zoom || 1,
      timestamp: Date.now(),
    };
  }

  /**
   * Restore UI state after a silent refresh
   * Should be called during app initialization
   * @param {Function} applyState - Function to apply the state
   */
  restoreUIState(applyState) {
    const wasSilentRefresh = sessionStorage.getItem("orbital_silent_refresh");
    if (!wasSilentRefresh) return;

    const savedState = sessionStorage.getItem("orbital_ui_state");
    if (!savedState) return;

    try {
      const uiState = JSON.parse(savedState);
      console.log(
        "[UpdateManager] Restoring UI state after silent refresh:",
        uiState,
      );

      if (applyState) {
        applyState(uiState);
      }

      // Restore scroll position
      if (uiState.scrollPosition) {
        window.scrollTo(uiState.scrollPosition.x, uiState.scrollPosition.y);
      }
    } catch (e) {
      console.error("[UpdateManager] Failed to restore UI state:", e);
    }

    // Clean up
    sessionStorage.removeItem("orbital_ui_state");
    sessionStorage.removeItem("orbital_silent_refresh");
  }

  /**
   * Show a non-blocking "suggest" banner
   * @param {string} message - Optional custom message
   */
  _showSuggestBanner(message) {
    this._showBanner({
      message: message || t("sync.update.available"),
      level: UpdateLevel.SUGGEST,
      dismissable: true,
    });
  }

  /**
   * Show a blocking "require" banner
   * @param {string} message - Optional custom message
   */
  _showRequireBanner(message) {
    this._showBanner({
      message: message || t("sync.update.required"),
      level: UpdateLevel.REQUIRE,
      dismissable: true,
    });
  }

  /**
   * Show the update banner
   * @param {Object} options - { message, level, dismissable }
   */
  _showBanner({ message, level, dismissable }) {
    // Get or create banner element
    this.bannerElement = document.getElementById("update-banner");
    if (!this.bannerElement) {
      console.warn("[UpdateManager] Banner element not found in DOM");
      return;
    }

    const messageEl = document.getElementById("update-banner-message");
    const refreshBtn = document.getElementById("update-banner-refresh");
    const dismissBtn = document.getElementById("update-banner-dismiss");

    if (messageEl) {
      messageEl.textContent = message;
    }

    // Set level class
    this.bannerElement.classList.remove("level-1", "level-2", "level-3");
    this.bannerElement.classList.add(`level-${level}`);

    // Show/hide dismiss button
    if (dismissBtn) {
      dismissBtn.style.display = dismissable ? "" : "none";
    }

    // Set up refresh button
    if (refreshBtn) {
      refreshBtn.onclick = () => {
        reloadApp();
      };
    }

    // Set up dismiss button
    if (dismissBtn && dismissable) {
      dismissBtn.onclick = () => {
        this._hideBanner();
        // Show sync-paused badge if dismissing a level 3 (Require) update (only when logged in)
        if (level === UpdateLevel.REQUIRE && _getCurrentUser()) {
          const syncPausedEl = document.getElementById("sync-paused");
          if (syncPausedEl) {
            syncPausedEl.classList.remove("hidden");
            syncPausedEl.onclick = () => reloadApp();
          }
        }
      };
    }

    // Show banner
    this.bannerElement.classList.remove("hidden");
    this.bannerElement.classList.add("visible");
  }

  /**
   * Hide the update banner
   */
  _hideBanner() {
    if (this.bannerElement) {
      this.bannerElement.classList.remove("visible");
      this.bannerElement.classList.add("hidden");
    }
  }

  /**
   * Disable sync (for level 3 updates)
   */
  _disableSync() {
    this.syncDisabled = true;
    stopSyncForUpdate();

    // Show sync paused badge (only when logged in)
    if (_getCurrentUser()) {
      const syncPausedEl = document.getElementById("sync-paused");
      if (syncPausedEl) {
        syncPausedEl.classList.remove("hidden");
        syncPausedEl.onclick = () => reloadApp();
      }
    }

    // Emit event for sync manager to stop syncing
    window.dispatchEvent(
      new CustomEvent("orbital:sync-disabled", {
        detail: { reason: "update-required" },
      }),
    );
    console.log("[UpdateManager] Sync disabled due to required update");
  }

  /**
   * Check if sync is disabled
   * @returns {boolean}
   */
  isSyncDisabled() {
    return this.syncDisabled || isSyncStoppedForUpdate();
  }

  /**
   * Get current app version
   * @returns {string}
   */
  getVersion() {
    return this.currentVersion;
  }
}

// Export singleton instance
export const updateManager = new UpdateManager();

// The engine's announcements (engine/sync/app-version.js announceUpdate).
on(Events.UPDATE_AVAILABLE, (update) => updateManager.handleUpdateAvailable(update));
